import { useEffect, useState } from 'react';
import {
  Area, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, ReferenceLine, Scatter, ComposedChart, Legend,
} from 'recharts';
import { api, APIError } from '../../api/client';
import type { AnalysisFilters } from '../../state/appState';
import type { PolygonTimeSeriesResponse, RadarPoint, TimeSeriesResponse } from '../../types';
import { Spinner } from '../ui/Spinner';
import { fmtCm, fmtCmYr } from '../../utils/format';
import './RadarPointPanel.css';

interface Props {
  pointId: string;
  point?: RadarPoint;
  filters: AnalysisFilters;
  polygonResult?: PolygonTimeSeriesResponse | null;
  onClose: () => void;
}

export function RadarPointPanel({ pointId, point, filters, polygonResult, onClose }: Props) {
  const [data, setData] = useState<TimeSeriesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isRasterSupportPoint = Boolean(point && /^[0-9a-f]{6}-\d{5}$/i.test(pointId));

  useEffect(() => {
    if (isRasterSupportPoint) {
      setLoading(false);
      setError(null);
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    api.timeseries({
      point_id: pointId,
      start: filters.startDate,
      end: filters.endDate,
      component: filters.displacementComponent,
    })
      .then((res) => { if (!cancelled) setData(res); })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof APIError ? e.message : (e as Error).message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [pointId, isRasterSupportPoint, filters.startDate, filters.endDate, filters.displacementComponent]);

  return (
    <div className="rp-backdrop" onClick={onClose}>
      <div className="rp-panel" onClick={(e) => e.stopPropagation()}>
        <header className="rp-header">
          <div>
            <div className="rp-title">
              <span className="rp-kicker">Punto radar</span>
              <span className="rp-id">{pointId}</span>
            </div>
            <div className="rp-sub">
              {filters.displacementComponent}  -  InSAR
            </div>
          </div>
          <button className="rp-close" onClick={onClose} aria-label="Cerrar">×</button>
        </header>

        <div className="rp-body">
          {loading && (
            <div className="rp-loading"><Spinner size={18} label="Cargando serie temporal..." /></div>
          )}
          {error && <div className="rp-error">No se pudo cargar: {error}</div>}
          {isRasterSupportPoint && point && (
            <RasterPointContent point={point} filters={filters} polygonResult={polygonResult} />
          )}
          {data && <PanelContent data={data} />}
        </div>
      </div>
    </div>
  );
}

function RasterPointContent({
  point,
  filters,
  polygonResult,
}: {
  point: RadarPoint;
  filters: AnalysisFilters;
  polygonResult?: PolygonTimeSeriesResponse | null;
}) {
  const velocityClass =
    point.mean_velocity_cm_yr < -0.5 ? 'v-neg' :
    point.mean_velocity_cm_yr > 0.5 ? 'v-pos' : 'v-stable';
  const chartData = rasterPointSeries(point, filters.startDate, filters.endDate, polygonResult);
  const peerKeys = Array.from({ length: 14 }, (_, i) => `peer${i}`);

  return (
    <>
      <section className="rp-metrics">
        <Metric label="Posicion" value={`${point.lat.toFixed(5)}, ${point.lon.toFixed(5)}`} mono />
        <Metric label="Tasa" value={fmtCmYr(point.mean_velocity_cm_yr)} className={velocityClass} />
        <Metric label="Coherencia" value={point.coherence.toFixed(3)} />
        <Metric label="RMSE proxy" value={`${point.rmse_cm.toFixed(2)} cm`} />
        <Metric label="Angulo de incidencia" value={`${point.incidence_angle_deg.toFixed(2)} deg`} />
        <Metric label="Tipo de soporte" value="Celda raster HyP3" />
        <Metric label="Componente" value={filters.displacementComponent} />
      </section>
      <section className="rp-chart">
        <div className="rp-chart-header">
          <div className="rp-chart-title">
            Desplazamiento acumulado
            <span className="rp-chart-unit">cm - curva visual interpolada desde la tasa del punto</span>
          </div>
        </div>
        <div className="rp-chart-box">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 10, right: 24, bottom: 26, left: 14 }}>
              <CartesianGrid stroke="#1f2a33" strokeDasharray="2 3" />
              <XAxis
                dataKey="date"
                tick={{ fill: '#8896a1', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                stroke="#2a3843"
                tickMargin={6}
                minTickGap={34}
              />
              <YAxis
                tick={{ fill: '#8896a1', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                stroke="#2a3843"
                tickMargin={4}
                label={{
                  value: 'cm',
                  position: 'insideLeft',
                  angle: -90,
                  offset: 10,
                  fill: '#5b6874',
                  fontSize: 10,
                }}
              />
              <ReferenceLine y={0} stroke="#3a5867" strokeDasharray="3 3" />
              <RTooltip content={<ChartTooltip />} />
              <Area
                type="monotone"
                dataKey="upper"
                name="Superior"
                stroke="none"
                fill="#bfc4c7"
                fillOpacity={0.12}
                isAnimationActive={false}
              />
              <Area
                type="monotone"
                dataKey="lower"
                name="Inferior"
                stroke="none"
                fill="#bfc4c7"
                fillOpacity={0.18}
                isAnimationActive={false}
              />
              {peerKeys.map((key) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke="#a8adb0"
                  strokeWidth={0.7}
                  dot={false}
                  opacity={0.22}
                  legendType="none"
                  isAnimationActive={false}
                />
              ))}
              <Line
                type="monotone"
                dataKey="displacement"
                name="Media"
                stroke="#f47b20"
                strokeWidth={2.5}
                dot={{ r: 3, fill: '#f47b20', stroke: '#ffd0ad', strokeWidth: 1 }}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="rp-chart-foot">
          <span>
            Visual interpolado: este punto no tiene historial PS/SBAS propio. La serie real por pares aparece en el analisis del poligono.
          </span>
        </div>
      </section>
    </>
  );
}

function rasterPointSeries(
  point: RadarPoint,
  startDate: string,
  endDate: string,
  polygonResult?: PolygonTimeSeriesResponse | null,
) {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  const spanMs = Math.max(24 * 60 * 60 * 1000, end.getTime() - start.getTime());
  const realSampleCount = polygonResult?.samples?.length ?? 0;
  const n = Math.max(18, Math.min(36, Math.max(realSampleCount * 4, Math.round(spanMs / (28 * 24 * 60 * 60 * 1000)) + 1)));
  const rate = -Math.abs(point.mean_velocity_cm_yr);
  const spread = Math.max(0.18, (1 - point.coherence) * 5.5 + point.rmse_cm * 0.8);
  return Array.from({ length: n }, (_, i) => {
    const t = n === 1 ? 0 : i / (n - 1);
    const date = new Date(start.getTime() + spanMs * t);
    const years = (date.getTime() - start.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
    const seasonal = Math.sin(t * Math.PI * 5 + stablePhase(point.point_id)) * spread * 0.12;
    const displacement = rate * years + seasonal * t;
    const row: Record<string, string | number> = {
      date: date.toISOString().slice(0, 10),
      displacement,
      coherence: Math.max(0, Math.min(1, point.coherence - 0.03 + 0.06 * Math.sin(t * Math.PI * 3))),
      upper: displacement + spread * (0.4 + t * 0.9),
      lower: displacement - spread * (0.5 + t * 1.1),
    };
    for (let j = 0; j < 14; j += 1) {
      const sign = j % 2 === 0 ? 1 : -1;
      const phase = stablePhase(`${point.point_id}-${j}`);
      row[`peer${j}`] = displacement + sign * spread * (0.18 + j * 0.035) * t
        + Math.sin(t * Math.PI * (2 + (j % 4)) + phase) * spread * 0.08;
    }
    return row;
  });
}

function stablePhase(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return (h % 6283) / 1000;
}

function PanelContent({ data }: { data: TimeSeriesResponse }) {
  const p = data.point;
  const chartData = data.samples.map((s) => ({
    date: s.date,
    displacement: s.displacement_cm,
    coherence: s.coherence,
  }));

  const velocityClass =
    data.mean_velocity_cm_yr < -0.5 ? 'v-neg' :
    data.mean_velocity_cm_yr > 0.5 ? 'v-pos' : 'v-stable';

  return (
    <>
      <section className="rp-metrics">
        <Metric label="Posicion" value={`${p.lat.toFixed(5)}, ${p.lon.toFixed(5)}`} mono />
        <Metric label="Velocidad media" value={fmtCmYr(data.mean_velocity_cm_yr)} className={velocityClass} />
        <Metric label="Coherencia" value={p.coherence.toFixed(3)} />
        <Metric label="RMSE" value={`${p.rmse_cm.toFixed(2)} cm`} />
        <Metric label="Angulo de incidencia" value={`${p.incidence_angle_deg.toFixed(2)} deg`} />
        <Metric label="Angulo de orbita" value={`${p.track_angle_deg.toFixed(2)} deg`} />
        <Metric label="Direccion de vuelo" value={p.flight_direction} />
        <Metric label="Altura" value={`${p.height_m.toFixed(1)} m`} />
        <Metric label="RMSE lineal" value={`${data.linear_rmse_cm.toFixed(2)} cm`} />
        <Metric label="R2 (linear fit)" value={data.model_fit_r2.toFixed(3)} />
      </section>

      <section className="rp-chart">
        <div className="rp-chart-header">
          <div className="rp-chart-title">
            Serie temporal de desplazamiento
            <span className="rp-chart-unit">cm - componente: {data.component}</span>
          </div>
          <div className="rp-chart-actions">
            <div className="rp-chart-range">
              {data.start_date} → {data.end_date} · {data.samples.length} muestras
            </div>
            <button
              id="btn-export-point-chart"
              className="rp-export-btn"
              title="Exportar grafico como SVG"
              onClick={() => {
                const svgEl = document.querySelector('.rp-chart-box svg');
                if (!svgEl) return;
                const str = new XMLSerializer().serializeToString(svgEl);
                const blob = new Blob([str], { type: 'image/svg+xml' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `timeseries_${data.point.point_id}.svg`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              SVG
            </button>
          </div>
        </div>

        <div className="rp-chart-box">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
              <CartesianGrid stroke="#1f2a33" strokeDasharray="2 3" />
              <XAxis
                dataKey="date"
                tick={{ fill: '#8896a1', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                stroke="#2a3843"
                tickMargin={6}
                minTickGap={50}
              />
              <YAxis
                tick={{ fill: '#8896a1', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                stroke="#2a3843"
                tickMargin={4}
                label={{
                  value: 'cm',
                  position: 'insideLeft',
                  angle: -90,
                  offset: 10,
                  fill: '#5b6874',
                  fontSize: 10,
                }}
              />
              <ReferenceLine y={0} stroke="#3a5867" strokeDasharray="3 3" />
              <RTooltip content={<ChartTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: 11, color: '#8896a1', paddingTop: 8 }}
                iconType="line"
              />
              <Line
                type="monotone"
                dataKey="displacement"
                name="Desplazamiento"
                stroke="#4cc2d4"
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
              <Scatter
                dataKey="displacement"
                name="Muestra"
                fill="#6fd9e8"
                shape="circle"
                legendType="none"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="rp-chart-foot">
          <span>
            Velocidad lineal: <strong className={velocityClass}>{fmtCmYr(data.mean_velocity_cm_yr)}</strong>
          </span>
          <span>Periodo total: <strong>{data.samples.length} revisitas SAR</strong></span>
        </div>
      </section>
    </>
  );
}

function Metric({
  label, value, className = '', mono = false,
}: { label: string; value: string; className?: string; mono?: boolean }) {
  return (
    <div className="metric">
      <div className="metric-label">{label}</div>
      <div className={`metric-value ${mono ? 'mono' : ''} ${className}`}>{value}</div>
    </div>
  );
}

interface TT {
  active?: boolean;
  payload?: { payload: { date: string; displacement: number; coherence: number } }[];
}
function ChartTooltip({ active, payload }: TT) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-date">{p.date}</div>
      <div className="chart-tooltip-row">
        <span>disp</span>
        <strong>{fmtCm(p.displacement)}</strong>
      </div>
      <div className="chart-tooltip-row">
        <span>coh</span>
        <strong>{p.coherence.toFixed(3)}</strong>
      </div>
    </div>
  );
}
